<template>
  <div>
    <v-dialog
      persistent
      no-click-animation
      transition="scale-transition"
      v-model="dialog"
      max-width="1200px"
      style="overflow-y: hidden !important"
    >
      <v-card rounded="lg" style="overflow-y: auto">
        <v-container class="py-md-5">
          <v-row class="px-4">
            <v-col cols="12" md="6" class="pb-0">
              <p class="text-h6 font-weight-bold mb-1">
                {{ localAccount?.account?.id ? 'Update' : 'Add' }} Account /
                Account number:
                <span class="primary--text">
                  {{ localAccount?.account?.mainAccountNumber }}
                </span>
              </p>
              <p class="font-weight-medium text--secondary mb-2">
                Update member's account before proceeding.
              </p>
            </v-col>
          </v-row>

          <v-card flat rounded="lg" class="py-0">
            <v-card :flat="true" rounded="lg" color="white" class="px-3 py-0">
              <v-row align="center" class="space-between mb-4">
                <v-col cols="12" sm="6" md="8" class="pb-0">
                  <v-tabs v-model="selectedUser">
                    <v-tab
                      v-for="(item, i) in getPersonsOnAccount"
                      :key="`person-${i}`"
                      class="text-capitalize font-weight-medium rounded-t-lg"
                    >
                      <v-icon
                        class="mr-2"
                        v-if="item.gender?.toLowerCase() === 'female'"
                      >
                        mdi-face-woman-outline</v-icon
                      >
                      <v-icon class="mr-2" v-else> mdi-face-man-outline</v-icon>
                      {{ item?.firstName }} {{ item?.lastName }}</v-tab
                    >
                  </v-tabs>
                </v-col>

                <v-col class="pb-0 text-center text-sm-right">
                  <v-btn
                    v-if="!integrationOptions.isIntegrated"
                    @click="
                      $refs.updateAccountNumberModal.open(
                        localAccount?.account?.id,
                        localAccount?.account?.mainAccountNumber
                      )
                    "
                    text
                    outlined
                    small
                    class="text-capitalize"
                    min-height="36"
                    >Edit Account Number</v-btn
                  >
                </v-col>
              </v-row>
            </v-card>

            <v-tabs
              slider-size="3"
              slider-color="primary darken-1"
              color="primary"
              class="mb-0 flat"
              background-color="grey lighten-4"
              show-arrows
              v-model="selectedView"
            >
              <v-tab
                v-for="item in tabItems"
                :key="item.title"
                :tab-value="item.value"
                active-class="primary darken-1 white--text"
                class="text-capitalize grey lighten-4"
                ><v-icon left>{{ item.icon }}</v-icon
                >{{ item.title }}</v-tab
              >
            </v-tabs>

            <v-card
              flat
              rounded="lg"
              class="pt-3 px-7"
              style="height: 480px; overflow-y: auto"
              :key="rerenderKey"
            >
              <v-row class="mt-0 py-0">
                <v-col cols="12" class="py-0">
                  <!-- Personal -->

                  <v-row class="mt-0">
                    <v-col cols="12" class="pa-2">
                      <v-alert
                        v-if="validationMessage"
                        type="error"
                        text
                        class="d-flex"
                      >
                        <p class="font-weight-bold mb-1">
                          Please ensure the form is filled out correctly
                        </p>
                        {{ validationMessage }}</v-alert
                      >
                      <personal-form
                        ref="personal"
                        v-show="selectedView === 'personal-details'"
                        :outlined="false"
                        :account="getSelectedPerson"
                        :personDetailsOnly="true"
                      />
                      <address-form
                        ref="address"
                        v-show="selectedView === 'address'"
                        :livingAddress="getSelectedPerson?.livingAddress"
                        :mailingAddress="getSelectedPerson?.mailingAddress"
                        @updateLiving="updateLivingAddress"
                        @updateMailing="updateMailingAddress"
                      />

                      <contact-form
                        ref="contacts"
                        v-if="selectedView === 'contact'"
                        @update="updateContacts"
                        :contacts="getSelectedPerson?.contact"
                      />

                      <employer-form
                        ref="employer"
                        v-if="selectedView === 'employer'"
                        @update="updateEmploymentDetails"
                        moduleName="Loan-Pro"
                        :employments="getSelectedPerson?.employer"
                      />

                      <connected-person-form
                        ref="connectedPerson"
                        v-if="selectedView === 'connected-person'"
                        @update="updateConnectedPersons"
                        :connectedPersons="getSelectedPerson?.connectedPersons"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
          </v-card>
        </v-container>
        <v-card-actions class="justify-end pb-4 px-12 pt-0">
          <v-btn @click="closeModal" depressed class="text-capitalize ml-3"
            >Cancel</v-btn
          >

          <v-btn
            depressed
            @click="updateAccountDetails"
            :loading="loading"
            :disabled="loading"
            color="primary"
            class="text-capitalize ml-3 shadow"
          >
            Continue
            <v-icon left>mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-actions>
        <!-- Update account number modal -->
        <UpdateAccountNumber
          @update="updateAccountNumber"
          ref="updateAccountNumberModal"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { removeNullAndEmptyArrays } from '@/lib/helpers';
import PersonalForm from '@/modules/shared/components/person-form/forms/person-form.vue';
import ContactForm from '@/modules/shared/components/person-form/forms/contact-form.vue';
import AddressForm from '@/modules/shared/components/person-form/forms/address-form.vue';
import ConnectedPersonForm from '@/modules/shared/components/person-form/forms/connected-person-form.vue';
import EmployerForm from '@/modules/shared/components/person-form/forms/employer-form.vue';
import UpdateAccountNumber from '@/modules/loanpro/components/loan-pro/Search/CondenseForm/UpdateAccountNumber.vue';
import AccountManagementService from '@/modules/loanpro/services/account-management.service';
import { cloneDeep, merge } from 'lodash';
import pageMixin from '@/mixins/page.mixin';
import { object, string, array, date, bool } from 'yup';

const accountManagementService = new AccountManagementService();
export default {
  mixins: [pageMixin],
  components: {
    PersonalForm,
    ContactForm,
    AddressForm,
    EmployerForm,
    ConnectedPersonForm,
    UpdateAccountNumber
  },

  props: {
    showEditAccountBtn: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      validationMessage: '',

      loading: false,
      dialog: false,
      step: null,
      selectedUser: 0,
      localAccount: {},
      account: {},
      integrationOptions: {
        isIntegrated: false,
        core: ''
      },
      rerenderKey: 1,
      tabItems: [
        {
          title: 'Personal Details',
          value: 'personal-details',
          required: true,
          icon: 'mdi-account-outline'
        },
        {
          title: 'Address Information',
          value: 'address',
          required: true,
          icon: 'mdi-home-outline'
        },
        {
          title: 'Contact Numbers',
          value: 'contact',
          icon: 'mdi-phone-plus-outline',
          required: true
        },
        {
          title: 'Employments',
          value: 'employer',
          required: true,
          icon: 'mdi-briefcase-outline'
        },
        {
          title: 'Connected Persons',
          value: 'connected-person',
          icon: 'mdi-account-group-outline',
          required: false
        }
      ],
      selectedView: 'personal-details',
      defaultAccount: {
        account: {
          branch: null,
          accountStatus: null,
          mainAccountNumber: '0000000'
        },
        persons: [
          {
            id: '',
            coreId: '',
            trn: '',
            lastName: 'New',
            firstName: 'Person',
            middleName: null,
            dateOfBirth: '',
            title: 'Dr',
            branchId: null,
            contact: [],
            livingAddress: {},
            employer: [],
            references: [],
            documents: [],
            personClass: 'primary',
            dateOfMembership: null
          }
        ]
      }
    };
  },

  watch: {
    // update the user data being edited once the user is selected
    selectedUser: {
      immediate: true,
      handler: function () {
        this.$refs.personal?.setDetails(this.getSelectedPerson);
        this.$refs.contacts?.setContacts(this.getSelectedPerson?.contact);
        this.$refs.employer?.setEmployments(this.getSelectedPerson?.employer);
        this.$refs.address?.setLivingAddress(
          this.getSelectedPerson?.livingAddress
        );
        this.$refs.address?.setMailingAddress(
          this.getSelectedPerson?.mailingAddress
        );
        this.$forceUpdate();
      }
    }
  },

  emits: ['accountSaved'],

  methods: {
    validatePersons() {
      // call validation methodf inside the personal component
      this.$refs.personal.validateData();
      const schema = object({
        // check for account number
        account: object({
          mainAccountNumber: string()
            .required('Account number is required')
            .test(
              'unique-account-check',
              'Account No. ${value} already exist',
              async function (value) {
                const result =
                  await accountManagementService.doesAccountNumberExist(
                    this.parent.id,
                    value
                  );
                return !result;
              }
            )
        }).required('Account number is required'),
        // Person array validation
        persons: array()
          .ensure()
          .min(1, 'At least one person should be on the account')
          .of(
            object({
              firstName: string().required('First name is required'),
              lastName: string().required('Last name is required'),
              alias: string().nullable().optional().max(14),
              trn: string()
                .required('TRN is required')
                .min(9)
                .max(14)
                .test(
                  'unique-trn-check',
                  'TRN ${value} already exist on another person',
                  async function (value) {
                    const result = await accountManagementService.doesTRNExist(
                      this.parent.id,
                      value
                    );
                    return !result;
                  }
                ),
              gender: string()
                .required('Gender is required')
                .oneOf(['Male', 'Female']),
              dateOfBirth: date()
                .typeError('Invalid date')
                .required('Date of birth is required'),
              maritalStatus: string()
                .required('Marital status is required')
                .oneOf([
                  'Single',
                  'Married',
                  'Divorced',
                  'Widowed',
                  'Separated'
                ]),
              emailAddress: string().email('Invalid email address').optional(),
              // check if contact is present
              contact: array()
                .ensure()
                .min(1, 'At least one contact is required per person')
                .of(
                  object({
                    type: string().required('Contact type is required'),
                    number: string().required('Contact number is required')
                  })
                ),
              // Check if living address is present
              livingAddress: object({
                street: string().required('Living address street is required'),
                city: string().required('Living address city is required'),
                country: string().required(
                  'Living address country is required'
                ),
                province: string().required(
                  'Living address province is required'
                ),
                zipCode: string()
                  .nullable()
                  .optional()
                  .min(5, 'Zip code must be at least 5 characters'),
                nationality: string().optional(),
                documentType: string().nullable().optional(),
                dateAtAddress: date()
                  .typeError('Invalid date')
                  .required('Date at address is required')
              }).required('Living address is required'),
              // Check if at least one employment is present
              employer: array()
                .ensure()
                .min(
                  1,
                  'At least one employment record is needed for an account.'
                )
                .of(
                  object({
                    sector: string().required('Employment sector is required'),
                    employer: string().nullable().optional(),
                    nameOfEmployer: string().required(
                      'Employer name is required'
                    ),
                    dateEmployed: date()
                      .typeError('Invalid date')
                      .required('Date of employment is required'),
                    isPrimary: bool(),
                    employmentStatus: string().required(
                      'Employment status is required'
                    ),
                    contactNumber: string().required(
                      'Employment contact number is required'
                    ),
                    email: string().email('Invalid email address').optional(),
                    profession: string().required(
                      'Employment profession is required'
                    ),
                    businessIndustry: string().required(
                      'Employment business industry is required'
                    ),
                    street: string().required('Employment street is required'),
                    city: string().required('Employment city is required'),
                    country: string().required(
                      'Employment country is required'
                    ),
                    state: string().required('Employment state is required')
                  })
                ),

              connectedPersons: array().of(
                object({
                  firstName: string()
                    .required('Connected person first name is required')
                    .max(
                      45,
                      "Connected person's first name cannot be greater than 45 characters"
                    ),
                  lastName: string()
                    .required('Connected person last name is required')
                    .max(
                      45,
                      "Connected person's last name cannot be greater than 45 characters"
                    ),
                  alias: string()
                    .max(
                      45,
                      "Connected person's alias cannot be greater than 45 characters"
                    )
                    .optional(),
                  contactNumber: string().required(
                    "Connected person's contact number is required"
                  ),
                  street: string()
                    .required("Connected person's street is required")
                    .max(
                      70,
                      "Connected person's street cannot be greater than 45 characters"
                    ),
                  city: string()
                    .required("Connected person's city is required")
                    .max(
                      45,
                      "Connected person's city cannot be greater than 45 characters"
                    ),
                  country: string().required(
                    "Connected person's country is required"
                  ),
                  state: string().required(
                    'Connected person state is required'
                  ),
                  relation: string().required(
                    'Connected person state is required'
                  ),
                  email: string()
                    .email('Invalid Connected person email address')
                    .optional()
                })
              )
            })
          )
      });

      //TODO: remove
      console.log('account: ', this.localAccount);
      return schema.validate(this.localAccount);
    },

    async closeModal() {
      this.dialog = false;
      this.validationMessage = '';
    },

    openModal(account, integrationOptions) {
      this.integrationOptions = integrationOptions || this.integrationOptions;
      this.dialog = true;
      this.account = account || this.defaultAccount;
      this.account?.persons?.forEach((e, i) => {
        if (e.dateOfMembership)
          e.dateOfMembership = e.dateOfMembership.split('T')?.[0];

        if (e.dateOfBirth) e.dateOfBirth = e.dateOfBirth.split('T')?.[0];

        if (!e.emailAddress) e.emailAddress = '';

        this.account.persons.splice(i, 1, e);
      });
      this.localAccount = cloneDeep(this.account);
    },

    async updateAccountDetails() {
      this.loading = true;
      this.validationMessage = '';

      try {
        // validate the data
        // Note that this will throw an error if validation fails using the catch section of this trycatch block
        await this.validatePersons();

        // //check to ensure at least one person on the account is a primary person class
        // const numberOfPrimaryPersons = this.localAccount.persons.filter(
        //   (el) => el.personClass === 'Main Applicant'
        // ).length;
        // if (!numberOfPrimaryPersons) {
        //   throw new Error(
        //     'At least one account holder person class must be Primary'
        //   );
        // }

        //remove branch object from request
        delete this.localAccount.account.branch;

        // check if id exist on localAccount.account
        // if it does, send update, if not send create
        if (!this.localAccount?.account?.id) {
          await accountManagementService.createAccount(
            removeNullAndEmptyArrays(this.localAccount)
          );
        } else {
          await accountManagementService.updateAccount(
            this.account?.account?.mainAccountNumber,
            removeNullAndEmptyArrays(this.localAccount)
          );
        }

        this.showSnack({
          color: 'success',
          title: `Account updated`,
          message: `The account was successfully updated`
        });

        this.$emit('accountSaved', this.localAccount);
      } catch (error) {
        const isValidationError = error.name === 'ValidationError';
        if (isValidationError) {
          //Select the tab the validation error occurred on
          if (error.path.includes('employer')) {
            this.selectedView = 'employer';
          } else if (error.path.includes('contact')) {
            this.selectedView = 'contact';
          } else if (error.path.includes('connectedPersons')) {
            this.selectedView = 'connected-person';
          } else if (
            error.path.includes('livingAddress') ||
            error.path.includes('mailingAddress')
          ) {
            this.selectedView = 'address';
          } else {
            this.selectedView = 'personal-details';
          }

          this.validationMessage = error.message;
        } else {
          this.showSnack({
            color: 'error',
            title: `${
              isValidationError ? 'Please check form' : 'Something went wrong'
            }`,
            timeout: 10000,
            message: error.response?.data?.message || error.message
          });
        }
      } finally {
        this.loading = false;
      }
    },

    async updateAccountNumber(num) {
      // Merge the account number with the account object
      this.localAccount = merge({}, this.localAccount, {
        account: {
          mainAccountNumber: num
        }
      });
    },
    deleteContact(contact) {
      const index = this.localAccount?.persons?.[
        this.selectedUser
      ]?.contact?.findIndex((item) => item.number === contact.number);

      this.localAccount?.persons?.[this.selectedUser]?.contact?.splice(
        index,
        1
      );
    },
    deleteEmployer(address) {
      const index = this.localAccount?.persons?.[
        this.selectedUser
      ]?.employer?.findIndex((item) => item.employer === address.employer);

      this.localAccount?.persons?.[this.selectedUser]?.employer?.splice(
        index,
        1
      );
    },
    updateLivingAddress(address) {
      const person = this.localAccount?.persons?.[this.selectedUser];
      if (person) {
        person.livingAddress = address;
      }
    },
    updateMailingAddress(address) {
      const person = this.localAccount?.persons?.[this.selectedUser];
      if (person) {
        person.mailingAddress = address;
      }
    },
    updateEmploymentDetails(employer) {
      const person = this.localAccount?.persons?.[this.selectedUser];
      if (person) {
        person.employer = employer;
      }
    },
    updateContacts(contact) {
      const person = this.localAccount?.persons?.[this.selectedUser];
      if (person) {
        person.contact = contact;
      }
    },

    /**
     * Get triggers when the connected persons are updated
     */
    updateConnectedPersons(connectedPersons) {
      console.log(connectedPersons);
      this.person.connectedPersons = connectedPersons;
    }
  },
  computed: {
    // Get list of persons on account
    getPersonsOnAccount() {
      return this.localAccount?.persons || [];
    },
    // Get the selected person based on tab value
    getSelectedPerson() {
      return this.getPersonsOnAccount?.[this.selectedUser];
    }
  }
};
</script>
